import z from "zod";

/**
 * Wraps a schema to treat empty strings as undefined and makes it optional.
 * Useful for form fields where empty input should be treated as "not provided".
 */
export function optionalField<T extends z.ZodTypeAny>(schema: T) {
	return z
		.preprocess((v) => (v === "" ? undefined : v), schema.optional())
		.optional();
}

export type Flatten<T extends readonly (readonly string[])[]> =
	T[number][number];

export type WithOptionalFields<T, K extends keyof T> = Omit<T, K> &
	Partial<Pick<T, K>>;

/**
 * Wraps an object schema with "all or nothing" validation for groups of fields.
 * If any field in a group has a value, all fields in that group become required.
 * Fields in groups are automatically made optional with empty-string preprocessing.
 */
export function withConditionalGroups<
	T extends z.ZodObject<z.ZodRawShape>,
	const G extends (keyof z.infer<T> & string)[][],
>(
	schema: T,
	groups: G,
	messages?: Partial<Record<Flatten<G>, string>>,
): z.ZodType<
	WithOptionalFields<z.infer<T>, Flatten<G>>,
	WithOptionalFields<z.input<T>, Flatten<G>>
> {
	const allGroupFields = new Set<string>(groups.flat());
	const shape = schema.shape as z.ZodRawShape;

	const newShape = Object.fromEntries(
		Object.entries(shape).map(([key, value]) => [
			key,
			allGroupFields.has(key) ? optionalField(value as z.ZodTypeAny) : value,
		]),
	) as z.ZodRawShape;

	return z.object(newShape).superRefine((val, ctx) => {
		for (const fields of groups) {
			const filledFields = fields.filter(
				(key) => val[key] !== undefined && val[key] !== "",
			);
			const emptyFields = fields.filter(
				(key) => val[key] === undefined || val[key] === "",
			);

			if (filledFields.length > 0 && emptyFields.length > 0) {
				for (const key of emptyFields) {
					ctx.addIssue({
						code: "custom",
						message:
							messages?.[key as Flatten<G>] ??
							`${key.replace(/([A-Z])/g, " $1").replace(/^./, (s) => s.toUpperCase())} is required`,
						path: [key],
					});
				}
			}
		}
	}) as unknown as z.ZodType<
		WithOptionalFields<z.infer<T>, Flatten<G>>,
		WithOptionalFields<z.input<T>, Flatten<G>>
	>;
}
